/**
 * Ícones do painel, desenhados aqui em vez de vir de biblioteca.
 *
 * Duas razões. A primeira é o histórico: `npm install` neste projeto já derrubou o build mais de uma
 * vez (o binário nativo do lightningcss sumindo), e não vale arriscar a árvore de dependências por
 * um punhado de glifos. A segunda é a regra que a própria biblioteca existiria para garantir:
 * **um só vocabulário visual**. Com todos passando pelo mesmo `<Glifo>`, largura de traço, tamanho e
 * terminação de linha são iguais por construção, não por disciplina.
 *
 * Convenção: `viewBox` de 24, traço de 1,75, sem preenchimento, pontas e junções arredondadas. A cor
 * vem de `currentColor`, então quem usa controla pelo `text-*` do Tailwind.
 *
 * São **decorativos**: `aria-hidden`. Todo ícone aqui acompanha texto visível ou vive dentro de um
 * controle que já tem nome acessível — ícone sozinho num botão precisa de `aria-label` em quem
 * chama (é o que o `IconButton` do `ui.tsx` obriga), não aqui.
 *
 * Há teste fixando as três propriedades que fazem um conjunto parecer um conjunto: mesmo vocabulário,
 * `aria-hidden` em todos, e **nenhum desenho repetido** — dois ícones idênticos com significados
 * diferentes são pior que ícone nenhum.
 */

import type { BlockKind } from '@merlin/flow-engine';
import type { ReactNode, SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Lado do quadrado, em px. O padrão serve para o texto de 14px do painel. */
  size?: number;
}

function Glifo({ size = 16, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

// --- blocos do fluxo -------------------------------------------------------

/** Balão de conversa: o bloco só fala e segue. */
export const IconeMensagem = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M20 14a2 2 0 0 1-2 2H8l-4 3.5V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
  </Glifo>
);

/** Cursor de texto: espera o visitante escrever. */
export const IconePergunta = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M4 7V5h16v2M12 5v14M9 19h6" />
  </Glifo>
);

/** Dois caminhos saindo de um ponto: cada botão é uma saída. */
export const IconeBotoes = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 3v6M12 9l-6 5v6M12 9l6 5v6" />
    <circle cx="12" cy="3" r="1.5" />
    <circle cx="6" cy="20" r="1.5" />
    <circle cx="18" cy="20" r="1.5" />
  </Glifo>
);

/** Bifurcação com o losango da decisão. */
export const IconeCondicao = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 3l4 4-4 4-4-4z" />
    <path d="M12 11v3M12 14H6v6M12 14h6v6" />
  </Glifo>
);

/** Chevrons de código. */
export const IconeCodigo = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M9 6l-5 6 5 6M15 6l5 6-5 6" />
  </Glifo>
);

/** Globo: a chamada sai do navegador do visitante. */
export const IconeWebhookNavegador = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </Glifo>
);

/** Servidor empilhado: a chamada sai do backend, onde ficam os segredos. */
export const IconeWebhookBackend = (props: IconProps) => (
  <Glifo {...props}>
    <rect x="3" y="4" width="18" height="7" rx="1.5" />
    <rect x="3" y="13" width="18" height="7" rx="1.5" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </Glifo>
);

/**
 * O ícone de cada tipo de bloco.
 *
 * Fica aqui, e não em `BLOCK_META`, porque `flow/mapping.ts` é `.ts` e não carrega JSX. O casamento
 * por `BlockKind` é exaustivo: tipo novo sem ícone não compila.
 */
export const BLOCK_ICONS: Record<BlockKind, (props: IconProps) => ReactNode> = {
  message: IconeMensagem,
  input: IconePergunta,
  buttons: IconeBotoes,
  condition: IconeCondicao,
  browserCode: IconeCodigo,
  browserWebhook: IconeWebhookNavegador,
  backendWebhook: IconeWebhookBackend,
};

// --- navegação do webchat --------------------------------------------------

/** Nós ligados: a tela do fluxo. */
export const IconeFluxo = (props: IconProps) => (
  <Glifo {...props}>
    <rect x="3" y="4" width="7" height="5" rx="1.5" />
    <rect x="14" y="15" width="7" height="5" rx="1.5" />
    <path d="M6.5 9v5.5a3 3 0 0 0 3 3H14" />
  </Glifo>
);

/** Colunas de um gráfico: a tela de dados. */
export const IconeDados = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M4 20V4M4 20h16" />
    <path d="M8.5 20v-6M13 20v-10M17.5 20v-4" />
  </Glifo>
);

/** Engrenagem simplificada: a tela de configurações. */
export const IconeConfig = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />
  </Glifo>
);

// --- seções das configurações ---------------------------------------------

/** Gotas de tinta: a aparência do widget. */
export const IconeEstilo = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 3c4.5 4 7 6.9 7 9.8A7 7 0 0 1 5 12.8C5 9.9 7.5 7 12 3z" />
    <path d="M9 13.5a3 3 0 0 0 3 3" />
  </Glifo>
);

/** Escudo: é a lista que autoriza, não uma lista qualquer de endereços. */
export const IconeDominios = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 3l7 3v6c0 4-3 7.2-7 9-4-1.8-7-5-7-9V6z" />
    <path d="M9 12l2 2 4-4" />
  </Glifo>
);

/** Chave: o segredo que só o servidor lê. */
export const IconeSegredos = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="8" cy="15" r="4" />
    <path d="M10.8 12.2L20 3M17 6l2.5 2.5M14.5 8.5L17 11" />
  </Glifo>
);

/** Ponteiro de medidor: o teto de conversas. */
export const IconeLimite = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M4 18a8 8 0 1 1 16 0" />
    <path d="M12 18l4-5" />
  </Glifo>
);

/** Etiqueta de script: o snippet que se cola no site. */
export const IconeInstalar = (props: IconProps) => (
  <Glifo {...props}>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M9 10l-2 2 2 2M14 10l2 2-2 2" />
  </Glifo>
);

/** Pessoas: os contatos capturados. */
export const IconeLeads = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M3 19a6 6 0 0 1 12 0" />
    <path d="M16 5.5a3.5 3.5 0 0 1 0 7M17.5 14.2A6 6 0 0 1 21 19" />
  </Glifo>
);

// --- ações do painel -------------------------------------------------------

/** Olho: abrir o preview. */
export const IconeOlho = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
    <circle cx="12" cy="12" r="2.75" />
  </Glifo>
);

/** Seta subindo de uma base: promover o rascunho para o ar. */
export const IconePublicar = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 16V4M12 4L7 9M12 4l5 5M4 19h16" />
  </Glifo>
);

/** Lixeira. */
export const IconeLixeira = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M4 6h16M9 6V4h6v2M6 6l1 14h10l1-14M10 10v6M14 10v6" />
  </Glifo>
);

/** Mais: acrescentar item a uma lista. */
export const IconeMais = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 5v14M5 12h14" />
  </Glifo>
);

/** Setas em círculo: recarregar os números. */
export const IconeAtualizar = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M20 11a8 8 0 0 0-13.7-5.2L3 9M3 5v4h4" />
    <path d="M4 13a8 8 0 0 0 13.7 5.2L21 15M21 19v-4h-4" />
  </Glifo>
);

/** Seta para uma base: baixar o CSV. */
export const IconeBaixar = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 4v11M12 15l-4-4M12 15l4-4M5 19h14" />
  </Glifo>
);

/** Duas folhas: copiar para a área de transferência. */
export const IconeCopiar = (props: IconProps) => (
  <Glifo {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M15 6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3" />
  </Glifo>
);

/**
 * Duas folhas com um "+": duplicar um bloco.
 *
 * Separado do `IconeCopiar` de propósito, ainda que a base do desenho seja a mesma. Copiar manda para
 * a área de transferência; duplicar cria um objeto novo no fluxo. O "+" é o que diz "passa a existir
 * mais um", e usar o mesmo glifo para as duas ações ensinaria a coisa errada em um dos dois lugares.
 */
export const IconeDuplicar = (props: IconProps) => (
  <Glifo {...props}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M15 6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3" />
    <path d="M15 12.5v5M12.5 15h5" />
  </Glifo>
);

/** Relógio com ponteiro para trás: o histórico de publicações. */
export const IconeHistorico = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </Glifo>
);

/** Porta com seta: sair da conta. */
export const IconeSair = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
    <path d="M10 8l-4 4 4 4M6 12h9" />
  </Glifo>
);

/** Chevron para baixo: abre o menu logo abaixo do gatilho. */
export const IconeChevron = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M6 9.5l6 6 6-6" />
  </Glifo>
);

// --- estados ---------------------------------------------------------------
//
// Existem para que tom de aviso não seja comunicado **só** por cor. Quem não distingue vermelho de
// âmbar via só duas caixas coloridas com texto dentro; com o glifo, a natureza do aviso chega por
// forma também. É a regra `color-not-only` da WCAG, e o mesmo par cor+forma que o Material usa.

/** Círculo com "i": informação. */
export const IconeInfo = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </Glifo>
);

/** Triângulo com "!": atenção. */
export const IconeAlerta = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M12 3.5L22 20H2z" />
    <path d="M12 10v4M12 17h.01" />
  </Glifo>
);

/** Círculo com "×": erro. */
export const IconeErro = (props: IconProps) => (
  <Glifo {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </Glifo>
);

/** Tique: deu certo. */
export const IconeCheck = (props: IconProps) => (
  <Glifo {...props}>
    <path d="M4.5 12.5l5 5 10-11" />
  </Glifo>
);
